import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
} from '@mui/material';
import {
  useGetExpensesQuery,
  useCreateExpenseMutation,
  useGetExpenseAccountsQuery,
} from '../../../store/api/financeApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';

function Expenses() {
  const today = new Date().toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(today.slice(0, 7) + '-01');
  const [toDate, setToDate] = useState(today);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const { data: expenses, isLoading, error } = useGetExpensesQuery({ fromDate, toDate });
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const { data: expenseAccounts } = useGetExpenseAccountsQuery();
  const [createExpense, { isLoading: saving }] = useCreateExpenseMutation();

  const [form, setForm] = useState({
    expenseDate: today,
    accountId: '',
    amount: '',
    paymentMethod: 'cash',
    paymentAccountId: '',
    referenceNo: '',
    notes: '',
  });

  const formatAmount = (value) => {
    const n = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
    } catch {
      return `${currency} ${n.toFixed(2)}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount || 0);
    if (!form.expenseDate || !form.accountId || !amt || amt <= 0) {
      alert('Date, expense account and amount are required.');
      return;
    }
    try {
      await createExpense({
        ...form,
        amount: amt,
        paymentAccountId: form.paymentAccountId || undefined,
        referenceNo: form.referenceNo || undefined,
        notes: form.notes || undefined,
      }).unwrap();
      setForm((prev) => ({
        ...prev,
        amount: '',
        referenceNo: '',
        notes: '',
      }));
    } catch (err) {
      alert(err?.data?.message || 'Error creating expense');
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 3 }}>
        Expenses
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TextField
          type="date"
          label="From"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          type="date"
          label="To"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Add Expense
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}
          >
            <TextField
              type="date"
              label="Date *"
              value={form.expenseDate}
              onChange={(e) => setForm((prev) => ({ ...prev, expenseDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              select
              label="Expense Account *"
              value={form.accountId}
              onChange={(e) => setForm((prev) => ({ ...prev, accountId: e.target.value }))}
              size="small"
              sx={{ minWidth: 260 }}
            >
              <MenuItem value="">Select expense account</MenuItem>
              {Array.isArray(expenseAccounts) &&
                expenseAccounts.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              type="number"
              label={`Amount (${currency}) *`}
              value={form.amount}
              onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              select
              label="Method *"
              value={form.paymentMethod}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  paymentMethod: e.target.value,
                }))
              }
              size="small"
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
            </TextField>
            <TextField
              select
              label="Payment Account (optional)"
              value={form.paymentAccountId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, paymentAccountId: e.target.value }))
              }
              size="small"
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Auto (by method)</MenuItem>
              {paymentAccounts?.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.code} - {acc.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Reference"
              value={form.referenceNo}
              onChange={(e) => setForm((prev) => ({ ...prev, referenceNo: e.target.value }))}
              size="small"
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              size="small"
              sx={{ minWidth: 260, flex: 1 }}
              multiline
              maxRows={3}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Saving...' : 'Save Expense'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Expenses List
          </Typography>
          {isLoading && <Typography>Loading...</Typography>}
          {error && (
            <Typography color="error">
              Error loading expenses
            </Typography>
          )}
          {!isLoading && !error && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expense Account</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Payment Account</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(expenses) && expenses.length > 0 ? (
                    expenses.map((exp) => (
                      <TableRow key={exp.id}>
                        <TableCell>
                          {exp.expenseDate
                            ? new Date(exp.expenseDate).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {exp.account
                            ? `${exp.account.code} - ${exp.account.name}`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {exp.paymentAccount
                            ? `${exp.paymentAccount.code} - ${exp.paymentAccount.name}`
                            : '-'}
                        </TableCell>
                        <TableCell>{exp.referenceNo || '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {formatAmount(exp.amount)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          No expenses found for this period.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default Expenses;

